/**
 * The heading that marks one section of the happening form.
 *
 * The happening dialog is long enough that people scroll it fast, and every section used to
 * announce itself with the same flat `text-lg font-semibold` line — nothing for the eye to
 * lock onto in motion. Three things fix that, and they are why this component exists rather
 * than a bare `<h3>`:
 *
 * 1. It sticks. While you are inside a section its heading stays pinned just under the dialog
 *    header, so "where am I" is always answered without scrolling back.
 * 2. It carries an icon. A shape is recognisable at a glance, well before a word is readable.
 * 3. It draws a full-width rule. A horizontal band is the thing peripheral vision catches
 *    while the page is moving.
 *
 * The sticky offset comes from `--lb-dialog-header-h`, which the dialog measures onto the form
 * (see `useMeasuredHeight`); the fallback keeps this usable anywhere else.
 */

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FormSectionHeadingProps {
    icon: LucideIcon;
    title: string;
    /** Small count or status shown next to the title, e.g. the number of participants. */
    badge?: string | number | null;
    /** Draws the badge as a warning rather than a neutral count. */
    badgeTone?: 'neutral' | 'warning' | 'danger';
    /** One line under the title. Keep it short — it competes with the fields below. */
    description?: string;
    /** Right-aligned controls (a toggle, an "add" button). */
    action?: React.ReactNode;
    className?: string;
}

const badgeToneClasses = {
    neutral: 'bg-muted text-muted-foreground',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-destructive/10 text-destructive',
};

export function FormSectionHeading({
    icon: Icon,
    title,
    badge,
    badgeTone = 'neutral',
    description,
    action,
    className,
}: FormSectionHeadingProps) {
    return (
        <div
            className={cn(
                'sticky z-20 -mx-6 mb-5 border-b border-border bg-background/95 px-6 py-2.5 backdrop-blur',
                className,
            )}
            style={{ top: 'var(--lb-dialog-header-h, 0px)' }}
        >
            <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="truncate text-sm font-semibold tracking-tight text-foreground">
                            {title}
                        </h3>
                        {badge !== null && badge !== undefined && badge !== '' && (
                            <span
                                className={cn(
                                    'shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                                    badgeToneClasses[badgeTone],
                                )}
                            >
                                {badge}
                            </span>
                        )}
                    </div>
                    {description && (
                        <p className="truncate text-xs text-muted-foreground">{description}</p>
                    )}
                </div>
                {action && <div className="shrink-0">{action}</div>}
            </div>
        </div>
    );
}
